import { useCallback, useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

interface Req { id: string; question: string; note: string | null; status: string; reply: string | null; email: string | null; createdAt: string }
interface Gap { id: string; question: string; answer: string; rating: number | null; found: boolean; createdAt: string }

/** What readers asked: requests for the team, and answers that fell short. */
export default function ReaderQuestionsTab({ projectSlug }: { projectSlug: string }) {
  const [requests, setRequests] = useState<Req[]>([]);
  const [gaps, setGaps] = useState<Gap[]>([]);
  const [replies, setReplies] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const r = await authFetch(`/api/docs/${projectSlug}/reader-questions`);
    if (!r.ok) { setError("Couldn't load reader questions."); return; }
    const d = await r.json();
    setRequests(d.requests); setGaps(d.gaps);
  }, [projectSlug]);
  useEffect(() => { load(); }, [load]);

  const close = async (id: string, status: "answered" | "wont_do") => {
    setError("");
    const r = await authFetch(`/api/docs/${projectSlug}/requests/${id}/close`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, reply: (replies[id] ?? "").trim() }),
    });
    if (!r.ok) setError(r.status === 422 ? "Write a reply first." : "Couldn't close that request.");
    load();
  };
  const reviewed = async (id: string) => {
    await authFetch(`/api/docs/${projectSlug}/reader-questions/${id}/review`, { method: "POST" });
    load();
  };
  const open = requests.filter((r) => r.status === "open");

  return (
    <div className="space-y-8 max-w-3xl">
      {error && <p role="alert" className="text-[13px] text-red-600">{error}</p>}
      <section>
        <h3 className="text-[14px] font-semibold text-slate-800 mb-1">Questions for the team ({open.length} open)</h3>
        {requests.length === 0 && <p className="text-[13px] text-slate-500">Nobody has asked the team anything yet.</p>}
        <ul className="space-y-3">
          {requests.map((r) => (
            <li key={r.id} className="border border-slate-200 rounded-lg p-3 text-[13px]">
              <p className="font-medium text-slate-800">{r.question}</p>
              <p className="text-[12px] text-slate-500">{r.email ?? "Unknown reader"} · {new Date(r.createdAt).toLocaleDateString()}</p>
              {r.note && <p className="mt-1 text-slate-600">{r.note}</p>}
              {r.status === "open" ? (
                <div className="mt-2 space-y-2">
                  <textarea aria-label="Reply" rows={2} value={replies[r.id] ?? ""} onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })}
                    placeholder="Reply to the reader" className="w-full px-2.5 py-1.5 text-[13px] border border-slate-200 rounded-lg" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => close(r.id, "answered")} className="px-3 py-1.5 text-[12px] font-medium rounded-lg bg-slate-800 text-white">Answer</button>
                    <button type="button" onClick={() => close(r.id, "wont_do")} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border border-slate-200 text-slate-600">Won't do</button>
                  </div>
                </div>
              ) : <p className="mt-1 text-slate-600">{r.status === "answered" ? "Answered" : "Won't do"}: {r.reply}</p>}
            </li>
          ))}
        </ul>
      </section>
      <section>
        <h3 className="text-[14px] font-semibold text-slate-800 mb-1">Docs gaps, last 30 days</h3>
        <p className="text-[12px] text-slate-500 mb-2">Questions the docs couldn't answer, and answers readers marked as bad.</p>
        {gaps.length === 0 && <p className="text-[13px] text-slate-500">No gaps to review.</p>}
        <ul className="space-y-2">
          {gaps.map((g) => (
            <li key={g.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 text-[13px]">
              <div className="min-w-0">
                <p className="text-slate-800">{g.question}</p>
                <p className="text-[12px] text-slate-500">{g.found ? "Marked as a bad answer" : "Not in the docs"} · {new Date(g.createdAt).toLocaleDateString()}</p>
              </div>
              <button type="button" onClick={() => reviewed(g.id)} className="shrink-0 text-[12px] text-slate-500 hover:text-slate-800">Mark reviewed</button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
